/**
 * Goal Consistency Utilities
 * Calculates habit consistency metrics for habit-to-goal linking feature
 */

/**
 * Format a date as YYYY-MM-DD string in local timezone
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
const formatDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate rolling consistency percentage for a single habit
 * @param {Object} completions - Completions object { [habitId]: { [dateString]: boolean } }
 * @param {string} habitId - The habit ID to calculate consistency for
 * @param {number} days - Number of days to look back (default: 7)
 * @returns {number} Consistency percentage 0-100
 */
export const calculateRollingConsistency = (completions, habitId, days = 7) => {
  if (!completions || !habitId) return 0;
  
  const habitCompletions = completions[habitId];
  if (!habitCompletions) return 0;
  
  const today = new Date();
  let completedDays = 0;
  
  for (let i = 0; i < days; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() - i);
    const dateStr = formatDateString(checkDate);
    
    if (habitCompletions[dateStr]) {
      completedDays++;
    }
  }
  
  return Math.round((completedDays / days) * 100);
};

/**
 * Calculate aggregate consistency for multiple habits linked to a goal
 * @param {Object} completions - Completions object
 * @param {string[]} linkedHabitIds - Array of habit IDs linked to the goal
 * @param {number} days - Number of days to look back (default: 7)
 * @returns {number|null} Aggregate consistency percentage 0-100, or null if no habits linked
 */
export const calculateGoalConsistency = (completions, linkedHabitIds, days = 7) => {
  // Handle undefined/empty arrays gracefully
  const habitIds = linkedHabitIds || [];
  if (habitIds.length === 0) return null;
  
  const today = new Date();
  let totalCompletions = 0;
  const totalPossible = habitIds.length * days;
  
  for (let i = 0; i < days; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() - i);
    const dateStr = formatDateString(checkDate);
    
    for (const habitId of habitIds) {
      if (completions?.[habitId]?.[dateStr]) {
        totalCompletions++;
      }
    }
  }
  
  return Math.round((totalCompletions / totalPossible) * 100);
};

/**
 * Get consistency history data for chart visualization
 * Returns an array of data points for recharts AreaChart
 * @param {Object} completions - Completions object
 * @param {string[]} linkedHabitIds - Array of habit IDs
 * @param {number} days - Number of days to show (7, 14, or 30)
 * @returns {Array<{rate: number, label: string}>} Chart data array
 */
export const getConsistencyHistory = (completions, linkedHabitIds, days = 7) => {
  const habitIds = linkedHabitIds || [];
  if (habitIds.length === 0) return [];
  
  const today = new Date();
  const data = [];
  
  for (let i = days - 1; i >= 0; i--) {
    const checkDate = new Date(today);
    checkDate.setDate(checkDate.getDate() - i);
    const dateStr = formatDateString(checkDate);
    
    // Count completions for this day
    const completedCount = habitIds.filter(
      habitId => completions?.[habitId]?.[dateStr]
    ).length;
    
    const rate = Math.round((completedCount / habitIds.length) * 100);
    
    // Format label based on range
    let label;
    if (days <= 7) {
      label = checkDate.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (days <= 14) {
      label = checkDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      label = checkDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    data.push({ rate, label, date: dateStr });
  }
  
  return data;
};

/**
 * Get color for consistency percentage based on thresholds
 * @param {number} percentage - Consistency percentage 0-100
 * @param {Object} colors - Theme colors object with error, warning, success
 * @returns {string} Color value
 */
export const getConsistencyColor = (percentage, colors) => {
  if (percentage === null || percentage === undefined) {
    return colors.textMuted;
  }
  if (percentage <= 33) {
    return colors.error;
  }
  if (percentage <= 66) {
    return colors.warning;
  }
  return colors.success;
};

